import type { CycleState, CycleUnavailableActions } from "#shared/reviewCycleSchemas.ts";
import { Button, type ButtonProps } from "@/components/ui/button.tsx";
import { Menu, MenuContent, MenuDescription, MenuItem, MenuLabel } from "@/components/ui/menu.tsx";
import { ArrowUturnLeftIcon, DocumentDuplicateIcon, LockClosedIcon, PencilIcon, TrashIcon } from "@heroicons/react/20/solid";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { ConfirmDialog } from "@packages/core-client/forms/ConfirmDialog.tsx";
import { LegacyRedirectModal } from "@packages/core-client/components/LegacyRedirectModal.tsx";
import { useManagedRedirectLabels } from "@packages/core-client/components/useManagedRedirectLabels.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useMemo, useState, type Key } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useDeleteReviewCycleMutationOptions, useDuplicateReviewCycleMutationOptions, useLockReviewCycleMutationOptions, useUnlockReviewCycleMutationOptions } from "../../api/reviewCyclesQuery.ts";
import { reviewCyclesI18NextInstanceKey } from "../../i18n/i18next.ts";

type ConfirmingAction = "lock" | "unlock" | "delete";

export interface CycleActionsMenuProps {
    cycleId: string;
    cycleName: string;
    state: CycleState;
    unavailableActions: CycleUnavailableActions;
    unsubmittedReviewCount: number;
    getEditHref: (cycleId: string) => string;
    isEditInApp?: boolean;
    onDeleted?: () => void;
    triggerSize?: ButtonProps["size"];
}

export function CycleActionsMenu({ cycleId, cycleName, state, unavailableActions, unsubmittedReviewCount, getEditHref, isEditInApp = false, onDeleted, triggerSize = "sq-sm" }: CycleActionsMenuProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("CycleActionsMenu", { i18n: i18nextInstance });
    const redirectLabels = useManagedRedirectLabels(i18nextInstance, "performance");
    const session = useSession();
    const navigate = useNavigate();

    const [confirming, setConfirming] = useState<ConfirmingAction | undefined>(undefined);
    const [editingCycleId, setEditingCycleId] = useState<string | undefined>(undefined);

    const input = useMemo(() => ({ orgId: session?.organizationId ?? "", cycleId }), [session?.organizationId, cycleId]);

    const { mutate: duplicateCycle } = useMutation(useDuplicateReviewCycleMutationOptions());
    const { mutate: deleteCycle, isPending: isDeleting } = useMutation(useDeleteReviewCycleMutationOptions());
    const { mutate: lockCycle, isPending: isLocking } = useMutation(useLockReviewCycleMutationOptions());
    const { mutate: unlockCycle, isPending: isUnlocking } = useMutation(useUnlockReviewCycleMutationOptions());

    const closeConfirm = useCallback(() => setConfirming(undefined), []);

    const openCycleEdit = useCallback(
        (id: string) => {
            if (isEditInApp) {
                void navigate(getEditHref(id));
            } else {
                setEditingCycleId(id);
            }
        },
        [getEditHref, isEditInApp, navigate]
    );

    const handleAction = useCallback(
        (key: Key) => {
            if (key === "edit") {
                openCycleEdit(cycleId);
            } else if (key === "duplicate") {
                duplicateCycle(input, {
                    onSuccess: ({ id }) => {
                        toast.success(t("duplicate.success"));
                        openCycleEdit(id);
                    },
                    onError: () => toast.error(t("duplicate.error"))
                });
            } else if (key === "lock" || key === "unlock" || key === "delete") {
                setConfirming(key);
            }
        },
        [cycleId, duplicateCycle, input, openCycleEdit, t]
    );

    const handleEditRedirectOpenChange = useCallback((isOpen: boolean) => setEditingCycleId(current => (isOpen ? current : undefined)), []);

    const handleConfirm = useCallback(() => {
        const mutate = confirming === "delete" ? deleteCycle : confirming === "lock" ? lockCycle : unlockCycle;

        mutate(input, {
            onSuccess: () => {
                closeConfirm();
                toast.success(t(`${confirming}.success`));

                if (confirming === "delete") {
                    onDeleted?.();
                }
            },
            onError: () => toast.error(t(`${confirming}.error`))
        });
    }, [closeConfirm, confirming, deleteCycle, input, lockCycle, onDeleted, t, unlockCycle]);

    const isLocked = state === "locked";
    const lockPairAction = isLocked ? "unlock" : "lock";
    const lockPairReason = unavailableActions[lockPairAction];

    const lockDescription = unsubmittedReviewCount > 0 ? t("lock.descriptionUnsubmitted", { count: unsubmittedReviewCount }) : t("lock.description");

    const confirm =
        confirming &&
        {
            delete: { title: t("delete.title", { name: cycleName }), description: t("delete.description"), confirmLabel: t("delete.confirm"), confirmIntent: "danger" as const, confirmationLabel: t("delete.confirmation") },
            lock: { title: t("lock.title"), description: lockDescription, confirmLabel: t("lock.confirm"), confirmIntent: "danger" as const, confirmationLabel: undefined },
            unlock: { title: t("unlock.title"), description: t("unlock.description"), confirmLabel: t("unlock.confirm"), confirmIntent: "primary" as const, confirmationLabel: undefined }
        }[confirming];

    return (
        <>
            <Menu>
                <Button intent="plain" size={triggerSize} aria-label={t("ariaLabel", { name: cycleName })} name="cycle-actions">
                    <EllipsisHorizontalIcon aria-hidden="true" />
                </Button>
                <MenuContent placement="bottom end" onAction={handleAction}>
                    <MenuItem id="edit" isDisabled={Boolean(unavailableActions.edit)} textValue={t("edit.label")}>
                        <PencilIcon aria-hidden="true" />
                        <MenuLabel>{t("edit.label")}</MenuLabel>
                        {unavailableActions.edit && <MenuDescription>{t(`unavailable.${unavailableActions.edit}`)}</MenuDescription>}
                    </MenuItem>
                    <MenuItem id="duplicate" isDisabled={Boolean(unavailableActions.duplicate)} textValue={t("duplicate.label")}>
                        <DocumentDuplicateIcon aria-hidden="true" />
                        <MenuLabel>{t("duplicate.label")}</MenuLabel>
                        {unavailableActions.duplicate && <MenuDescription>{t(`unavailable.${unavailableActions.duplicate}`)}</MenuDescription>}
                    </MenuItem>
                    <MenuItem id={lockPairAction} isDisabled={Boolean(lockPairReason)} textValue={t(`${lockPairAction}.label`)}>
                        {isLocked ? <ArrowUturnLeftIcon aria-hidden="true" /> : <LockClosedIcon aria-hidden="true" />}
                        <MenuLabel>{t(`${lockPairAction}.label`)}</MenuLabel>
                        {lockPairReason && <MenuDescription>{t(`unavailable.${lockPairReason}`)}</MenuDescription>}
                    </MenuItem>
                    <MenuItem id="delete" intent="danger" isDisabled={Boolean(unavailableActions.delete)} textValue={t("delete.label")}>
                        <TrashIcon aria-hidden="true" />
                        <MenuLabel>{t("delete.label")}</MenuLabel>
                        {unavailableActions.delete && <MenuDescription>{t(`unavailable.${unavailableActions.delete}`)}</MenuDescription>}
                    </MenuItem>
                </MenuContent>
            </Menu>

            <LegacyRedirectModal
                isOpen={Boolean(editingCycleId)}
                onOpenChange={handleEditRedirectOpenChange}
                eyebrow={redirectLabels.modalEyebrow}
                title={redirectLabels.modalTitle}
                body={redirectLabels.modalBody}
                cancelLabel={redirectLabels.modalCancelLabel}
                continueLabel={redirectLabels.modalContinueLabel}
                continueHref={editingCycleId ? getEditHref(editingCycleId) : ""}
            />

            {confirm && (
                <ConfirmDialog
                    isOpen
                    title={confirm.title}
                    description={confirm.description}
                    confirmLabel={confirm.confirmLabel}
                    cancelLabel={t("cancel")}
                    confirmIntent={confirm.confirmIntent}
                    confirmationLabel={confirm.confirmationLabel}
                    isConfirming={isDeleting || isLocking || isUnlocking}
                    onConfirm={handleConfirm}
                    onCancel={closeConfirm}
                    confirmName={`confirm-${confirming}-cycle`}
                />
            )}
        </>
    );
}
